import React, { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, Calendar, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { useDemoTour } from '../contexts/DemoTourContext';

// Steps where the fake cursor should auto-click something
const CURSOR_CLICKS = {
  // Setup wizard — cursor interactions (click inputs to simulate typing, save buttons only at end)
  'setup-welcome':       { selector: '[data-tour="wizard-get-started"]',                    delay: 55000 },
  'setup-pms':           { selector: '[data-tour="wizard-pms-step"] input[type="text"]',    delay: 4000  },
  'setup-clearinghouse': { selector: '[data-tour="wizard-clearinghouse-step"] input',       delay: 4000  },
  'setup-automation':    { selector: '[data-tour="wizard-save-automation"]',                delay: 55000 },
  // Settings steps — click WaveOrtho (most relevant for ortho demo audience)
  'open-dental-sync':   { selector: 'input[name="pmsSystem"][value="waveortho"]',   delay: 4000 },
  // Multi-location: hover over the practice switcher dropdown
  'multi-location':     { selector: '[data-tour="practice-switcher"] select',        delay: 3000 },
  // Dashboard: click Run Now to trigger the robot animation
  'workflow-morning':   { selector: '[data-tour="claim-robot-run"]',                 delay: 5000 },
  // Workflow steps
  'workflow-preauth':    { selector: '[data-tour="sarah-preauth-row"]',           delay: 1500 },
  'workflow-new-claim':  { selector: '[data-tour="sarah-claim-row"]',             delay: 1200 },
  'workflow-submitted':  { selector: '[data-tour="demo-submit-button"]',          delay: 800  },
  'workflow-appeal':     { selector: '[data-tour="appeal-section"]',              delay: 4000 },
  'workflow-payment':    { selector: '[data-tour="sarah-era-payment"]',           delay: 900  },
  'era-posting':         { selector: '[data-tour="sarah-era-payment"]',           delay: 1200 },
  'patient-list':        { selector: '[data-tour="sarah-patient-row"]',           delay: 2000 },
};

// Steps where the highlight should switch to a different target mid-step
// delay = ms after step start to switch; selector = new target element
const STEP_TARGET_SWITCHES = {
  'workflow-morning': { delay: 8000, selector: '[data-tour="claim-robot-header"]' },
};

const HIGHLIGHT_PADDING = 10;

const DemoTour = () => {
  const {
    isActive, isPaused, currentStep, currentStepIndex, totalSteps,
    showEndScreen, voiceEnabled, setVoiceEnabled,
    startTour, endTour, pauseTour, resumeTour, nextStep, prevStep, setNavigate
  } = useDemoTour();

  const [targetRect, setTargetRect] = React.useState(null);
  const [cursor, setCursor] = React.useState({ visible: false, x: 0, y: 0, clicking: false });
  const cursorTimers = useRef([]);
  const isPausedRef = useRef(isPaused);
  const navigate = useNavigate();

  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate, setNavigate]);

  const findAndSetTarget = useCallback(() => {
    if (!currentStep) return;
    if (currentStep.route && window.location.pathname !== currentStep.route.split('?')[0]) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(currentStep.target);
    if (el) {
      el.scrollIntoView({ behavior: 'instant', block: currentStep.scrollBlock || 'center' });
      setTimeout(() => {
        const r = el.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) return;
        const vh = window.innerHeight;
        const vw = window.innerWidth;
        const top = Math.max(0, r.top - HIGHLIGHT_PADDING);
        const left = Math.max(0, r.left - HIGHLIGHT_PADDING);
        const maxHeight = vh - top - 8;
        const maxWidth = vw - left - 8;
        setTargetRect({
          top,
          left,
          width: Math.min(r.width + HIGHLIGHT_PADDING * 2, maxWidth),
          height: Math.min(r.height + HIGHLIGHT_PADDING * 2, maxHeight),
        });
      }, 150);
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  // Fake cursor — moves to target element and clicks for key steps
  useEffect(() => {
    cursorTimers.current.forEach(clearTimeout);
    cursorTimers.current = [];

    const config = CURSOR_CLICKS[currentStep?.id];
    if (!isActive || !config) {
      setCursor(c => ({ ...c, visible: false }));
      return;
    }

    const tryMoveCursor = (attempt = 0) => {
      const el = document.querySelector(config.selector);
      if (!el) {
        if (attempt < 12) {
          const t = setTimeout(() => tryMoveCursor(attempt + 1), 400);
          cursorTimers.current.push(t);
        }
        return;
      }
      const rect = el.getBoundingClientRect();
      const tx = rect.left + rect.width / 2;
      const ty = rect.top + rect.height / 2;

      // Appear at bottom-center, glide to target, click
      setCursor({ visible: true, x: window.innerWidth / 2, y: window.innerHeight * 0.8, clicking: false });
      const t1 = setTimeout(() => setCursor({ visible: true, x: tx, y: ty, clicking: false }), 150);
      const t2 = setTimeout(() => setCursor({ visible: true, x: tx, y: ty, clicking: true }), 950);
      const t3 = setTimeout(() => {
        el.click();
        setCursor({ visible: true, x: tx, y: ty, clicking: false });
      }, 1100);
      const t4 = setTimeout(() => setCursor(c => ({ ...c, visible: false })), 1800);
      cursorTimers.current.push(t1, t2, t3, t4);
    };

    const t0 = setTimeout(() => tryMoveCursor(), config.delay);
    cursorTimers.current.push(t0);

    return () => { cursorTimers.current.forEach(clearTimeout); };
  }, [currentStep?.id, isActive]);

  // Claims step transitions are handled entirely inside Claims.js (state-based, not DOM manipulation)

  // Patient row auto-click is handled by the fake cursor above

  // Auto-scroll appeal letter slowly
  useEffect(() => {
    if (currentStep?.id !== 'appeal-button' && currentStep?.id !== 'workflow-appeal') return;
    let retryTimer = null;
    let scrollInterval = null;
    let scrollStartTimer = null;

    const tryScroll = () => {
      const letterEl = document.querySelector('[data-tour="appeal-letter"]');
      if (!letterEl) {
        retryTimer = setTimeout(tryScroll, 600);
        return;
      }
      let scrollContainer = letterEl.parentElement;
      while (scrollContainer && scrollContainer !== document.body) {
        const style = window.getComputedStyle(scrollContainer);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') break;
        scrollContainer = scrollContainer.parentElement;
      }
      if (!scrollContainer || scrollContainer === document.body) return;
      letterEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      scrollStartTimer = setTimeout(() => {
        scrollInterval = setInterval(() => {
          if (isPausedRef.current) return;
          scrollContainer.scrollTop += 1;
          if (scrollContainer.scrollTop >= scrollContainer.scrollHeight - scrollContainer.clientHeight) {
            clearInterval(scrollInterval);
          }
        }, 35);
      }, 1500);
    };

    retryTimer = setTimeout(tryScroll, 3000);

    return () => {
      clearTimeout(retryTimer);
      clearTimeout(scrollStartTimer);
      if (scrollInterval) clearInterval(scrollInterval);
    };
  }, [currentStep?.id]);

  // Auto-scroll financial AI card slowly
  useEffect(() => {
    if (currentStep?.id !== 'workflow-financial-ai') return;
    let scrollInterval = null;
    let scrollStartTimer = null;
    let retryTimer = null;

    const tryScroll = () => {
      const el = document.querySelector('[data-tour="financial-ai"]');
      if (!el) { retryTimer = setTimeout(tryScroll, 600); return; }
      let scrollContainer = el.parentElement;
      while (scrollContainer && scrollContainer !== document.body) {
        const style = window.getComputedStyle(scrollContainer);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') break;
        scrollContainer = scrollContainer.parentElement;
      }
      if (!scrollContainer || scrollContainer === document.body) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      scrollStartTimer = setTimeout(() => {
        scrollInterval = setInterval(() => {
          if (isPausedRef.current) return;
          scrollContainer.scrollTop += 1;
          if (scrollContainer.scrollTop >= scrollContainer.scrollHeight - scrollContainer.clientHeight) {
            clearInterval(scrollInterval);
          }
        }, 40);
      }, 1800);
    };

    retryTimer = setTimeout(tryScroll, 1500);
    return () => {
      clearTimeout(retryTimer);
      clearTimeout(scrollStartTimer);
      if (scrollInterval) clearInterval(scrollInterval);
    };
  }, [currentStep?.id]);

  useEffect(() => {
    if (!isActive || !currentStep) return;
    setTargetRect(null);
    const t1 = setTimeout(findAndSetTarget, 300);
    const t2 = setTimeout(findAndSetTarget, 800);
    const t3 = setTimeout(findAndSetTarget, 1500);
    const t4 = setTimeout(findAndSetTarget, 2500);
    const t5 = setTimeout(findAndSetTarget, 4000);
    const t6 = setTimeout(findAndSetTarget, 6000);

    // Mid-step target switch — moves highlight to a different element after a delay
    const switchConfig = STEP_TARGET_SWITCHES[currentStep.id];
    let tSwitch = null;
    if (switchConfig) {
      tSwitch = setTimeout(() => {
        const el = document.querySelector(switchConfig.selector);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          const r = el.getBoundingClientRect();
          if (r.width === 0 || r.height === 0) return;
          const top = Math.max(0, r.top - HIGHLIGHT_PADDING);
          const left = Math.max(0, r.left - HIGHLIGHT_PADDING);
          setTargetRect({
            top,
            left,
            width: Math.min(r.width + HIGHLIGHT_PADDING * 2, window.innerWidth - left - 8),
            height: Math.min(r.height + HIGHLIGHT_PADDING * 2, window.innerHeight - top - 8),
          });
        }, 200);
      }, switchConfig.delay);
    }

    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      clearTimeout(t4); clearTimeout(t5); clearTimeout(t6);
      if (tSwitch) clearTimeout(tSwitch);
    };
  }, [isActive, currentStep, findAndSetTarget]);

  if (!isActive) return null;

  // End screen
  if (showEndScreen) {
    return (
      <div style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.75)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #0f766e 0%, #134e4a 100%)',
          borderRadius: 20,
          padding: '52px 44px',
          maxWidth: 480,
          width: '90%',
          textAlign: 'center',
          color: '#fff',
          boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
          border: '1px solid rgba(255,255,255,0.12)',
        }}>
          <div style={{ fontSize: 52, marginBottom: 18 }}>✅</div>
          <h2 style={{ fontSize: 26, fontWeight: 800, marginBottom: 12, lineHeight: 1.2 }}>
            That's FlowRCM in Action
          </h2>
          <p style={{ fontSize: 15, color: '#99f6e4', lineHeight: 1.7, marginBottom: 32 }}>
            Your billing team spends hours doing what FlowRCM handles in seconds — catching denials before submission, generating appeals automatically, and recovering revenue you'd otherwise lose.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <a
              href="https://flowrcmdental.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: '#fff',
                color: '#0f766e',
                padding: '14px 28px',
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 15,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Calendar size={17} />
              Book a Free Demo Call
            </a>
            <button
              onClick={() => { endTour(); setTimeout(startTour, 100); }}
              style={{
                background: 'rgba(255,255,255,0.15)',
                color: '#fff',
                padding: '12px 28px',
                borderRadius: 10,
                fontWeight: 600,
                fontSize: 14,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Play size={14} /> Watch Again
            </button>
            <button
              onClick={endTour}
              style={{
                background: 'transparent',
                color: '#99f6e4',
                padding: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Explore on your own
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentStep) return null;

  const progress = ((currentStepIndex + 1) / totalSteps) * 100;

  return (
    <>
      {/* Glowing highlight border */}
      {targetRect && (
        <div
          style={{
            position: 'fixed',
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            zIndex: 9991,
            borderRadius: 10,
            border: '3px solid #14b8a6',
            boxShadow: '0 0 0 4px rgba(20,184,166,0.2), 0 0 24px rgba(20,184,166,0.7)',
            pointerEvents: 'none',
            animation: 'tourGlow 2s ease-in-out infinite',
          }}
        />
      )}

      {/* Fixed bottom bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9995,
        background: 'rgba(10, 50, 50, 0.97)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(45, 212, 191, 0.25)',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.35)',
      }}>
        {/* Progress bar along very top of bar */}
        <div style={{ height: 3, background: 'rgba(255,255,255,0.1)' }}>
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #14b8a6, #2dd4bf)',
            transition: 'width 0.4s ease',
          }} />
        </div>

        {/* Main row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 20px',
          height: 52,
        }}>
          {/* Step info */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              color: '#5eead4',
              letterSpacing: '0.08em',
              whiteSpace: 'nowrap',
              textTransform: 'uppercase',
            }}>
              {currentStepIndex + 1}/{totalSteps}
            </span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {currentStep.title}
            </span>
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {/* Back */}
            <button
              onClick={prevStep}
              disabled={currentStepIndex === 0}
              title="Previous step"
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                color: '#fff',
                padding: '6px 10px',
                borderRadius: 6,
                cursor: currentStepIndex === 0 ? 'not-allowed' : 'pointer',
                opacity: currentStepIndex === 0 ? 0.35 : 1,
                display: 'flex', alignItems: 'center', gap: 3,
                fontSize: 12,
              }}
            >
              <ChevronLeft size={13} /> Back
            </button>

            {/* Pause/Play */}
            <button
              onClick={isPaused ? resumeTour : pauseTour}
              title={isPaused ? 'Resume' : 'Pause'}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                color: '#fff',
                padding: '6px 10px',
                borderRadius: 6,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center',
              }}
            >
              {isPaused ? <Play size={13} /> : <Pause size={13} />}
            </button>

            {/* Next */}
            <button
              onClick={nextStep}
              style={{
                background: '#14b8a6',
                border: 'none',
                color: '#fff',
                padding: '6px 14px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              {currentStepIndex === totalSteps - 1 ? 'Finish' : 'Next'} <ChevronRight size={13} />
            </button>

            {/* Divider */}
            <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.15)', margin: '0 2px' }} />

            {/* Voice toggle */}
            <button
              onClick={() => setVoiceEnabled(v => !v)}
              title={voiceEnabled ? 'Mute narration' : 'Enable narration'}
              style={{
                background: 'none',
                border: 'none',
                color: voiceEnabled ? '#2dd4bf' : 'rgba(255,255,255,0.35)',
                padding: '6px 8px',
                borderRadius: 6,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center',
              }}
            >
              {voiceEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>

            {/* Close */}
            <button
              onClick={endTour}
              title="Exit tour"
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.4)',
                padding: '6px 8px',
                borderRadius: 6,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center',
              }}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Fake cursor */}
      {cursor.visible && (
        <div style={{
          position: 'fixed',
          left: cursor.x,
          top: cursor.y,
          transform: `translate(-4px, -4px) scale(${cursor.clicking ? 0.8 : 1})`,
          transition: 'left 0.75s cubic-bezier(0.4,0,0.2,1), top 0.75s cubic-bezier(0.4,0,0.2,1), transform 0.12s',
          zIndex: 10001,
          pointerEvents: 'none',
        }}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="white" stroke="#0f766e" strokeWidth="1.5" strokeLinejoin="round">
            <path d="M4 2l14 8.5-6.5 1L8 19z" />
          </svg>
          {cursor.clicking && (
            <div style={{
              position: 'absolute', top: -12, left: -12,
              width: 44, height: 44, borderRadius: '50%',
              border: '2px solid rgba(20,184,166,0.7)',
              animation: 'cursorRipple 0.45s ease-out forwards',
            }} />
          )}
        </div>
      )}

      <style>{`
        @keyframes tourGlow {
          0%, 100% { box-shadow: 0 0 0 4px rgba(20,184,166,0.15), 0 0 16px rgba(20,184,166,0.5); }
          50% { box-shadow: 0 0 0 6px rgba(20,184,166,0.3), 0 0 32px rgba(20,184,166,0.9); }
        }
        @keyframes cursorRipple {
          0%   { transform: scale(0.5); opacity: 1; }
          100% { transform: scale(2);   opacity: 0; }
        }
      `}</style>
    </>
  );
};

export default DemoTour;
